import React, { createContext, useContext, ReactNode } from "react";
import { TimeFormatOption } from "../../../types/account/enum";
import { RepeatDropdownOptionsType } from "../../../types/block/enum";

interface DateModalContextValue {
  // Date/Time values
  dateTimeStart: Date | null;
  dateTimeEnd: Date | null;
  isAllDay: boolean;
  timeFormat: TimeFormatOption;

  // Setters
  setStartDateTime: (date: Date) => void;
  setEndDateTime: (date: Date) => void;
  setIsAllDay: (isAllDay: boolean) => void;

  // Picker states
  isTimeStartOpen: boolean;
  setIsTimeStartOpen: (isOpen: boolean) => void;
  isTimeEndOpen: boolean;
  setIsTimeEndOpen: (isOpen: boolean) => void;
  isDateStartOpen: boolean;
  setIsDateStartOpen: (isOpen: boolean) => void;
  isDateEndOpen: boolean;
  setIsDateEndOpen: (isOpen: boolean) => void;

  // Picker handlers
  onTimeStartChange: (date: Date) => void;
  onTimeEndChange: (date: Date) => void;
  onDateStartChange: (date: Date) => void;
  onDateEndChange: (date: Date) => void;

  // Actions
  handleDateClear: () => void;
  handleAddDateTime: () => void;
  handleIsAllDay: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleRepeatChange: (value: RepeatDropdownOptionsType) => void;
  handleOnDelete: (event: React.MouseEvent) => void;

  // Refs
  repeatModalRef: React.RefObject<HTMLDivElement>;
  repeatAddModalRef: React.RefObject<HTMLDivElement>;

  // Task related
  recurrence: string[] | null;
  isTask: boolean;
  isNewBlock: boolean;
}

const DateModalContext = createContext<DateModalContextValue | undefined>(undefined);

export const useDateModalContext = () => {
  const context = useContext(DateModalContext);
  if (context === undefined) {
    throw new Error("useDateModalContext must be used within a DateModalProvider");
  }
  return context;
};

interface DateModalProviderProps {
  children: ReactNode;
  value: DateModalContextValue;
}

export const DateModalProvider: React.FC<DateModalProviderProps> = ({ children, value }) => {
  return <DateModalContext.Provider value={value}>{children}</DateModalContext.Provider>;
};

export default DateModalContext;
